import classNames from "classnames";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg, { KuikaSvgProps } from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { toMidSentence } from "./quick-labels";
import { IDebouncedCommit, useDebouncedCommit } from "./use-debounced-commit";

/** Typing in an inline input commits after this pause (and on blur / Enter). */
export const INPUT_COMMIT_DELAY_MS: number = 400;
/** A burst of stepper clicks commits once, this long after the last click. */
export const STEPPER_COMMIT_DELAY_MS: number = 300;
/** Option lists longer than this get a search box. */
const SEARCH_THRESHOLD: number = 7;
/**
 * The data glyph the bar uses for binding: on the "Bind to data" button and before a bound chip's source
 * name — a glyph end users read as "data", not a symbol notation.
 */
export const QUICK_DATA_ICON: string = "database";
export const QUICK_DATA_ICON_TYPE: KuikaSvgProps["type"] = "datasource-icons";
/** Tallest a quick-prop popover grows before it scrolls. */
export const QUICK_POPOVER_HEIGHT_CAP: number = 420;

// ── Open state ──────────────────────────────────────────────────────────────────────────────────

/**
 * Bumped by the overlay when every floating piece must close (a click on the canvas, a drag starting).
 * Only the fallback for controls rendered without a `QuickOpenProvider` (standalone tests): under a
 * provider its `resetSignal` does this job.
 */
export const QuickDismissContext: React.Context<number> = React.createContext<number>(0);

/**
 * Which bar popover is open — ONE owner for the whole selection bar, so opening any popover (quick-prop
 * list, sticky editor, colour picker, style tag, "+N", ⋯, the breadcrumb menu) closes every other one.
 * Keys are per-control; a key opened inside a `QuickOpenScope` is `<scope>/<id>`, and a scope's own popover
 * stays open while one of its children is open (the "+N" popover and the folded controls inside it).
 */
export interface IQuickOpenStore {
  openKey: string | null;
  setOpenKey: (update: (prev: string | null) => string | null) => void;
}

export const QuickOpenContext: React.Context<IQuickOpenStore | null> = React.createContext<IQuickOpenStore | null>(
  null
);

const QuickOpenScopeContext: React.Context<string> = React.createContext<string>("");

export interface IQuickOpenProviderProps {
  /** Every change closes whatever is open (a canvas click, a drag, a new selection). */
  resetSignal: number;
  children: React.ReactNode;
}

export const QuickOpenProvider = ({ resetSignal, children }: IQuickOpenProviderProps): JSX.Element => {
  const [openKey, setOpenKeyState] = useState<string | null>(null);
  // Reset during render (not in an effect), so the frame after a reset never shows the old popover.
  const [seenSignal, setSeenSignal] = useState<number>(resetSignal);
  if (seenSignal !== resetSignal) {
    setSeenSignal(resetSignal);
    setOpenKeyState(null);
  }
  const store: IQuickOpenStore = useMemo((): IQuickOpenStore => ({ openKey, setOpenKey: setOpenKeyState }), [openKey]);
  return <QuickOpenContext.Provider value={store}>{children}</QuickOpenContext.Provider>;
};

export interface IQuickOpenScopeProps {
  /** The key of the popover whose content this is (from `useQuickOpenKey`). */
  scopeKey: string;
  children: React.ReactNode;
}

/** Popovers opened inside a scope nest under it: opening one keeps the scope's own popover open. */
export const QuickOpenScope = ({ scopeKey, children }: IQuickOpenScopeProps): JSX.Element => (
  <QuickOpenScopeContext.Provider value={scopeKey}>{children}</QuickOpenScopeContext.Provider>
);

let quickOpenKeyCounter: number = 0;

/** `useQuickOpenState` plus the control's own key, for a popover that hosts a `QuickOpenScope`. */
export function useQuickOpenKey(): [boolean, (open: boolean) => void, string] {
  const store: IQuickOpenStore | null = useContext(QuickOpenContext);
  const scope: string = useContext(QuickOpenScopeContext);
  const dismissSignal: number = useContext(QuickDismissContext);
  const idRef: React.MutableRefObject<string> = useRef<string>("");
  if (idRef.current === "") {
    quickOpenKeyCounter += 1;
    idRef.current = `q${quickOpenKeyCounter}`;
  }
  const key: string = scope === "" ? idRef.current : `${scope}/${idRef.current}`;

  // Fallback without a provider: a local flag that closes on the dismiss signal.
  const [isLocalOpen, setIsLocalOpen] = useState<boolean>(false);
  useEffect((): void => {
    setIsLocalOpen(false);
  }, [dismissSignal]);

  if (store === null) {
    return [isLocalOpen, setIsLocalOpen, key];
  }
  const openKey: string | null = store.openKey;
  const isOpen: boolean = openKey !== null && (openKey === key || openKey.startsWith(`${key}/`));
  const setOpen = (open: boolean): void => {
    store.setOpenKey((prev: string | null): string | null => {
      if (open) {
        return key;
      }
      // Close only what is still ours (this popover or one nested in it); back to the enclosing scope.
      if (prev !== null && (prev === key || prev.startsWith(`${key}/`))) {
        return scope === "" ? null : scope;
      }
      return prev;
    });
  };
  return [isOpen, setOpen, key];
}

/** A bar popover's open flag: opening it closes every other bar popover (see `QuickOpenContext`). */
export function useQuickOpenState(): [boolean, (open: boolean) => void] {
  const [isOpen, setOpen] = useQuickOpenKey();
  return [isOpen, setOpen];
}

// ── Tooltip ─────────────────────────────────────────────────────────────────────────────────────

export interface IQuickTipProps {
  label: string;
  shortcut?: string;
  /** Hide the tooltip (e.g. while the control's popover is open). */
  isSuppressed?: boolean;
  children: React.ReactElement;
}

/**
 * Forwards its ref to the tooltip trigger: a `QuickPopover` wraps `QuickTip > QuickChip` in
 * `Popover.Trigger asChild`, whose Slot anchors the popover through this ref — dropping it leaves Radix
 * with no anchor and the popover parked off-screen. The trigger's own props are deliberately not
 * forwarded: each chip toggles its popover in its own onClick, and a second toggle from the Slot would
 * close it again in the same click.
 */
export const QuickTip = React.forwardRef<HTMLElement, IQuickTipProps>(
  ({ label, shortcut, isSuppressed, children }: IQuickTipProps, ref: React.ForwardedRef<HTMLElement>): JSX.Element => {
    // Any open bar popover silences every bar tooltip: a tooltip left over from the chip the pointer
    // crossed on its way would otherwise sit on top of the popover the user is working in.
    const openStore: IQuickOpenStore | null = useContext(QuickOpenContext);
    const isAnyPopoverOpen: boolean = openStore !== null && openStore.openKey !== null;
    return (
      <KuikaTooltip
        ref={ref}
        content={
          isSuppressed || isAnyPopoverOpen ? null : (
            <span className="kuika-selection-overlay__tip">
              {label}
              {shortcut && <kbd className="kuika-selection-overlay__tip-kbd">{shortcut}</kbd>}
            </span>
          )
        }
        position="top"
        bgcolor="inverse"
        keepMounted
      >
        {children}
      </KuikaTooltip>
    );
  }
);
QuickTip.displayName = "QuickTip";

// ── Lock ────────────────────────────────────────────────────────────────────────────────────────

export interface IQuickLockProps {
  /** Why the wrapped controls cannot be used right now; null renders them as they are. */
  reason: string | null;
  testId: string;
  children: React.ReactNode;
}

/**
 * Disables every control inside (a `fieldset disabled` reaches buttons and inputs alike) and explains why
 * in a tooltip on a focusable wrapper — a disabled button receives no hover of its own.
 */
export const QuickLock = ({ reason, testId, children }: IQuickLockProps): JSX.Element => {
  if (reason === null) {
    return <>{children}</>;
  }
  return (
    <KuikaTooltip
      content={<span className="kuika-selection-overlay__tip">{reason}</span>}
      position="top"
      bgcolor="inverse"
    >
      <span className="kuika-quick-lock" tabIndex={0} aria-disabled="true" aria-label={reason} data-testid={testId}>
        <fieldset className="kuika-quick-lock__fieldset" disabled>
          {children}
        </fieldset>
      </span>
    </KuikaTooltip>
  );
};

// ── Chip ────────────────────────────────────────────────────────────────────────────────────────

export interface IQuickChipProps {
  testId: string;
  ariaLabel: string;
  /** KuikaSvg name (common set unless `iconType` says otherwise). */
  icon?: string;
  iconType?: "common" | "arrows" | "styling-panel-icons";
  /** Muted leading key, e.g. "Width". */
  keyLabel?: string;
  /** Leading glyph instead of an icon (e.g. "⚡", "+"). */
  glyph?: string;
  value?: React.ReactNode;
  /** The value is a placeholder ("Not set"): muted. */
  isEmpty?: boolean;
  /** Muted trailing detail (tabular figures). */
  meta?: string;
  /** Bound to data: shows the data glyph before the value, which is then the bound field/source name. */
  isBound?: boolean;
  count?: number;
  isWarning?: boolean;
  hasPopup?: boolean;
  isOpen?: boolean;
  /** Extra inline style for the value (e.g. a font preview). */
  valueStyle?: React.CSSProperties;
  /**
   * `compact` is for the bar's fixed right-hand section, where every px is taken from the quick props: the
   * value is capped tighter, and with no value the chip is its icon alone (a square, no chevron) — the
   * tooltip and the aria-label carry what it is.
   */
  variant?: "default" | "compact";
  onClick?: () => void;
}

export const QuickChip = React.forwardRef<HTMLButtonElement, IQuickChipProps>(
  (
    {
      testId,
      ariaLabel,
      icon,
      iconType,
      keyLabel,
      glyph,
      value,
      isEmpty,
      meta,
      isBound,
      count,
      isWarning,
      hasPopup,
      isOpen,
      valueStyle,
      variant = "default",
      onClick,
      ...rest
    }: IQuickChipProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => {
    const hasValue: boolean = value !== undefined && value !== null && value !== "";
    const isIconOnly: boolean = variant === "compact" && !hasValue && icon !== undefined;
    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        className={classNames("kuika-quick-chip", {
          "kuika-quick-chip--open": isOpen,
          "kuika-quick-chip--compact": variant === "compact",
          "kuika-quick-chip--icon-only": isIconOnly
        })}
        aria-label={ariaLabel}
        aria-haspopup={hasPopup ? "dialog" : undefined}
        aria-expanded={hasPopup ? isOpen === true : undefined}
        data-testid={testId}
        onClick={onClick}
      >
        {icon && (
          <KuikaSvg
            name={icon}
            type={iconType}
            width={14}
            height={14}
            color="currentColor"
            className="kuika-quick-chip__icon"
            aria-hidden="true"
          />
        )}
        {glyph && (
          <span className="kuika-quick-chip__icon" aria-hidden="true">
            {glyph}
          </span>
        )}
        {keyLabel && <span className="kuika-quick-chip__key">{keyLabel}</span>}
        {isBound && (
          <KuikaSvg
            name={QUICK_DATA_ICON}
            type={QUICK_DATA_ICON_TYPE}
            width={14}
            height={14}
            color="currentColor"
            className="kuika-quick-chip__bound"
            aria-hidden="true"
          />
        )}
        {hasValue && (
          <span
            className={classNames("kuika-quick-chip__value", { "kuika-quick-chip__value--empty": isEmpty })}
            style={valueStyle}
          >
            {value}
          </span>
        )}
        {meta && <span className="kuika-quick-chip__meta">{meta}</span>}
        {count !== undefined && count > 0 && <span className="kuika-quick-chip__count">{count}</span>}
        {isWarning && <span className="kuika-quick-chip__warn" title="Not set" data-testid={`${testId}-warn`} />}
        {hasPopup && !isIconOnly && (
          <KuikaSvg
            name="chevron-down"
            type="arrows"
            width={12}
            height={12}
            color="currentColor"
            className="kuika-quick-chip__chevron"
            aria-hidden="true"
          />
        )}
      </button>
    );
  }
);
QuickChip.displayName = "QuickChip";

// ── Popover ─────────────────────────────────────────────────────────────────────────────────────

export type QuickPopoverVariant = "list" | "editor";

export interface IQuickPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId: string;
  content: React.ReactNode;
  /**
   * The body's layout. Every quick popover is the overlay's surface (`KuikaPopover tone="inverse"`, the
   * bar's own themed surface); `list` (default) is a list the overlay draws itself, `editor` an editor
   * body that mounts the property panel's own controls.
   */
  variant?: QuickPopoverVariant;
  /** Keep the popover open on outside clicks (an editor whose own pickers portal elsewhere). */
  isSticky?: boolean;
  align?: "start" | "center" | "end";
  children: React.ReactElement;
}

/** The canvas region the overlay's popovers flip/shift inside, like the ⋯ menu. */
const canvasBoundary = (): Element | undefined => document.querySelector(".kuika_center") ?? undefined;

const maxPopoverHeight = (): number => {
  const boundary: Element | undefined = canvasBoundary();
  const height: number = boundary ? boundary.getBoundingClientRect().height : window.innerHeight;
  // Room for the bar and the gaps around it; the popover flips to whichever side is taller.
  return Math.max(160, Math.min(QUICK_POPOVER_HEIGHT_CAP, height / 2 - 24));
};

export const QuickPopover = ({
  open,
  onOpenChange,
  testId,
  content,
  variant = "list",
  isSticky,
  align = "start",
  children
}: IQuickPopoverProps): JSX.Element => (
  <KuikaPopover
    tone="inverse"
    visible={open}
    onVisibleChange={onOpenChange}
    position="bottom"
    align={align}
    sideOffset={8}
    collisionPadding={8}
    collisionBoundary={open ? canvasBoundary() : undefined}
    preventCloseOnClickOutside={isSticky}
    content={
      open ? (
        <div
          className={variant === "list" ? "kuika-quick-pop kuika-quick-pop--scroll" : "kuika-quick-editor"}
          data-testid={testId}
          style={{ maxHeight: maxPopoverHeight() }}
        >
          {content}
        </div>
      ) : null
    }
  >
    {children}
  </KuikaPopover>
);

// ── Option list (title · search · rows with check · footer) ────────────────────────────────────

export interface IQuickOption {
  value: string;
  label: string;
  sub?: string;
  /** Inline style for the label (a font name rendered in its font). */
  labelStyle?: React.CSSProperties;
}

export interface IQuickOptionListProps {
  title: string;
  options: IQuickOption[];
  /** The checked option — or options, for a multi-valued list (stacked style tags). */
  current: string | string[] | null | undefined;
  onPick: (value: string) => void;
  /** Defaults to "more than 7 options". */
  isSearchable?: boolean;
  emptyText?: string;
  optionTestId?: (option: IQuickOption) => string;
  onHover?: (value: string | null) => void;
  footer?: React.ReactNode;
}

export const QuickOptionList = ({
  title,
  options,
  current,
  onPick,
  isSearchable,
  emptyText = "Nothing to choose",
  optionTestId,
  onHover,
  footer
}: IQuickOptionListProps): JSX.Element => {
  const [query, setQuery] = useState<string>("");
  const hasSearch: boolean = isSearchable ?? options.length > SEARCH_THRESHOLD;
  const isChecked = (value: string): boolean => (Array.isArray(current) ? current.includes(value) : value === current);
  const visible: IQuickOption[] = useMemo((): IQuickOption[] => {
    const q: string = query.trim().toLowerCase();
    if (!q) {
      return options;
    }
    return options.filter((o: IQuickOption): boolean => o.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <>
      <div className="kuika-quick-pop__title">{title}</div>
      {hasSearch && (
        // The designer's own search field (the kit input with a search glyph), in the bar's inverse tone.
        <div className="kuika-quick-pop__search">
          <KuikaInput
            tone="inverse"
            size="sm"
            leftIcon={<KuikaSvg name="search" width={14} height={14} color="currentColor" aria-hidden="true" />}
            placeholder="Search..."
            autoComplete="off"
            aria-label={`Search ${toMidSentence(title)}`}
            value={query}
            allowClear
            autoFocus
            onChange={(_event: React.ChangeEvent<HTMLInputElement>, next: string | number): void =>
              setQuery(String(next))
            }
          />
        </div>
      )}
      <div className="kuika-quick-pop__list" role="listbox" aria-label={title}>
        {visible.map((option: IQuickOption): JSX.Element => (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={isChecked(option.value)}
            className="kuika-quick-pop__item"
            data-testid={optionTestId?.(option)}
            onClick={(): void => onPick(option.value)}
            onMouseEnter={onHover ? (): void => onHover(option.value) : undefined}
            onMouseLeave={onHover ? (): void => onHover(null) : undefined}
          >
            <span className="kuika-quick-pop__item-text">
              <span className="kuika-quick-pop__item-label" style={option.labelStyle}>
                {option.label}
              </span>
              {option.sub && <span className="kuika-quick-pop__item-sub">{option.sub}</span>}
            </span>
            {isChecked(option.value) && (
              <KuikaSvg
                name="check"
                width={14}
                height={14}
                color="currentColor"
                className="kuika-quick-pop__check"
                aria-hidden="true"
              />
            )}
          </button>
        ))}
        {visible.length === 0 && <div className="kuika-quick-pop__empty">{query ? "No matches" : emptyText}</div>}
      </div>
      {footer && (
        <>
          <div className="kuika-quick-pop__separator" role="separator" />
          {footer}
        </>
      )}
    </>
  );
};

export interface IQuickAddRowProps {
  label: string;
  testId: string;
  onClick: () => void;
}

export const QuickAddRow = ({ label, testId, onClick }: IQuickAddRowProps): JSX.Element => (
  <button
    type="button"
    className="kuika-quick-pop__item kuika-quick-pop__item--add"
    data-testid={testId}
    onClick={onClick}
  >
    <KuikaSvg name="plus" width={14} height={14} color="currentColor" aria-hidden="true" />
    {label}
  </button>
);

// ── Inline text input ──────────────────────────────────────────────────────────────────────────

/** Fewest characters the inline field is sized for (an empty field still shows "Not set"). */
export const INLINE_INPUT_MIN_CHARS: number = 4;
/** Most characters the inline field is sized for; the well's `max-width` caps the pixels either way. */
export const INLINE_INPUT_MAX_CHARS: number = 18;

/**
 * The inline field's width in characters: its text (or the placeholder when empty) plus one for the
 * caret, clamped to [min, max]. Drives the `size` attribute — the fallback where CSS `field-sizing:
 * content` is unsupported; where it is supported the browser sizes the field to its content itself.
 */
export function computeInlineInputWidth(text: string, min: number, max: number): number {
  const wanted: number = text.length + 1;
  return Math.max(min, Math.min(max, wanted));
}

export interface IQuickInputProps {
  value: string;
  placeholder?: string;
  ariaLabel: string;
  testId: string;
  /** Muted leading key naming the property ("Content", "Placeholder"), so the value never reads as a hint. */
  keyLabel?: string;
  /** A control at the input's end (the "Bind to data" button). Not inside a `<label>`: a button is labelable. */
  trailing?: React.ReactNode;
  onCommit: (value: string) => void;
}

/** Commits on blur / Enter and, while typing, after a 400ms pause — one write per pause, not per key. */
export const QuickInput = ({
  value,
  placeholder,
  ariaLabel,
  testId,
  keyLabel,
  trailing,
  onCommit
}: IQuickInputProps): JSX.Element => {
  const [draft, setDraft] = useState<string>(value);
  const fieldRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
  const lastCommittedRef: React.MutableRefObject<string> = useRef<string>(value);
  const commit = (next: string): void => {
    if (next === lastCommittedRef.current) {
      return;
    }
    lastCommittedRef.current = next;
    onCommit(next);
  };
  const debounced: IDebouncedCommit<string> = useDebouncedCommit<string>(commit, INPUT_COMMIT_DELAY_MS);

  // The value changed underneath us (undo, the panel, a collaborator): show it unless mid-edit.
  useEffect((): void => {
    if (!debounced.isPending()) {
      lastCommittedRef.current = value;
      setDraft(value);
    }
  }, [value, debounced]);

  // The well is one target: a press on its key or padding focuses the field (the trailing button keeps its own).
  // Only a press in the well's own DOM counts: React bubbles a portal's events through the component tree, so a
  // press inside the trailing button's editor popover (portalled to <body>) reaches this handler too.
  const focusField = (event: React.MouseEvent<HTMLSpanElement>): void => {
    const target: Element = event.target as Element;
    if (!event.currentTarget.contains(target) || target === fieldRef.current || target.closest("button") !== null) {
      return;
    }
    event.preventDefault();
    fieldRef.current?.focus();
  };

  const sizeText: string = draft === "" ? (placeholder ?? "") : draft;

  return (
    <span className="kuika-quick-input" onMouseDown={focusField}>
      {keyLabel && (
        <span className="kuika-quick-input__key" aria-hidden="true">
          {keyLabel}
        </span>
      )}
      <input
        ref={fieldRef}
        className="kuika-quick-input__field"
        size={computeInlineInputWidth(sizeText, INLINE_INPUT_MIN_CHARS, INLINE_INPUT_MAX_CHARS)}
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-testid={testId}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          setDraft(e.target.value);
          debounced.schedule(e.target.value);
        }}
        onBlur={debounced.flush}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => {
          if (e.key === "Enter") {
            debounced.flush();
          }
        }}
      />
      {trailing}
    </span>
  );
};

export interface IQuickBindButtonProps {
  ariaLabel: string;
  testId: string;
  isOpen: boolean;
  onClick: () => void;
  /** Pointer entered / left the button — lets the field around it quiet its own tooltip meanwhile. */
  onHoverChange?: (isHovered: boolean) => void;
}

/**
 * The data button at the end of an inline value ("Bind to data"): opens the property panel's editor for
 * the same property, so binding it to data stays one click away. Forwards its ref — it is a
 * `QuickPopover` trigger (through `QuickTip`).
 */
export const QuickBindButton = React.forwardRef<HTMLButtonElement, IQuickBindButtonProps>(
  (
    { ariaLabel, testId, isOpen, onClick, onHoverChange, ...rest }: IQuickBindButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={classNames("kuika-quick-input__bind", { "kuika-quick-input__bind--open": isOpen })}
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      data-testid={testId}
      onClick={onClick}
      onPointerEnter={onHoverChange ? (): void => onHoverChange(true) : undefined}
      onPointerLeave={onHoverChange ? (): void => onHoverChange(false) : undefined}
    >
      <KuikaSvg
        name={QUICK_DATA_ICON}
        type={QUICK_DATA_ICON_TYPE}
        width={14}
        height={14}
        color="currentColor"
        aria-hidden="true"
      />
    </button>
  )
);
QuickBindButton.displayName = "QuickBindButton";

// ── Stepper ─────────────────────────────────────────────────────────────────────────────────────

export interface IQuickStepperProps {
  value: number | null;
  ariaLabel: string;
  testId: string;
  keyLabel?: string;
  step?: number;
  min?: number;
  max?: number;
  onCommit: (value: number) => void;
}

const clamp = (n: number, min: number | undefined, max: number | undefined): number => {
  let out: number = n;
  if (min !== undefined && out < min) {
    out = min;
  }
  if (max !== undefined && out > max) {
    out = max;
  }
  return out;
};

/**
 * The value one step from `current`. An unset value ("Auto") starts at the floor: the first + sets `min`
 * (or 1 when there is none), the first − sets `min` (or 0).
 */
export function stepQuickValue(
  current: number | null,
  direction: 1 | -1,
  step: number,
  min: number | undefined,
  max: number | undefined
): number {
  if (current === null) {
    const start: number = direction === 1 ? (min ?? 1) : (min ?? 0);
    return clamp(start, min, max);
  }
  return clamp(current + direction * step, min, max);
}

/** What an unset stepper shows: the component falls back to its own default. */
export const STEPPER_EMPTY_LABEL: string = "Auto";

/**
 * key − value +. A burst of clicks commits ONCE (300ms after the last) — one undo step; clicking the value
 * edits it inline (Enter / blur commits, Esc cancels).
 */
export const QuickStepper = ({
  value,
  ariaLabel,
  testId,
  keyLabel,
  step = 1,
  min,
  max,
  onCommit
}: IQuickStepperProps): JSX.Element => {
  const [shown, setShown] = useState<number | null>(value);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>("");
  const debounced: IDebouncedCommit<number> = useDebouncedCommit<number>(onCommit, STEPPER_COMMIT_DELAY_MS);

  useEffect((): void => {
    if (!debounced.isPending()) {
      setShown(value);
    }
  }, [value, debounced]);

  const bump = (direction: 1 | -1): void => {
    const next: number = stepQuickValue(shown, direction, step, min, max);
    setShown(next);
    debounced.schedule(next);
  };

  const finishEdit = (isCommit: boolean): void => {
    setIsEditing(false);
    if (!isCommit) {
      return;
    }
    const parsed: number = Number.parseFloat(draft);
    if (Number.isFinite(parsed)) {
      const next: number = clamp(parsed, min, max);
      setShown(next);
      debounced.cancel();
      onCommit(next);
    }
  };

  return (
    <div className="kuika-quick-stepper" role="group" aria-label={ariaLabel} data-testid={testId}>
      {keyLabel && (
        <span className="kuika-quick-stepper__key" aria-hidden="true" data-testid={`${testId}-key`}>
          {keyLabel}
        </span>
      )}
      <button
        type="button"
        className="kuika-quick-stepper__button"
        aria-label={`Decrease ${toMidSentence(ariaLabel)}`}
        data-testid={`${testId}-decrease`}
        onClick={(): void => bump(-1)}
      >
        −
      </button>
      {isEditing ? (
        <input
          className="kuika-quick-stepper__field"
          inputMode="decimal"
          aria-label={ariaLabel}
          data-testid={`${testId}-field`}
          value={draft}
          autoFocus
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setDraft(e.target.value)}
          onBlur={(): void => finishEdit(true)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (e.key === "Enter") {
              finishEdit(true);
            } else if (e.key === "Escape") {
              e.preventDefault();
              finishEdit(false);
            }
          }}
        />
      ) : (
        <button
          type="button"
          className={classNames("kuika-quick-stepper__value", {
            "kuika-quick-stepper__value--empty": shown === null
          })}
          aria-label={`Edit ${toMidSentence(ariaLabel)}`}
          data-testid={`${testId}-value`}
          onClick={(): void => {
            debounced.flush();
            setDraft(shown === null ? "" : String(shown));
            setIsEditing(true);
          }}
        >
          {shown === null ? STEPPER_EMPTY_LABEL : shown}
        </button>
      )}
      <button
        type="button"
        className="kuika-quick-stepper__button"
        aria-label={`Increase ${toMidSentence(ariaLabel)}`}
        data-testid={`${testId}-increase`}
        onClick={(): void => bump(1)}
      >
        +
      </button>
    </div>
  );
};

// ── Toggle ──────────────────────────────────────────────────────────────────────────────────────

export interface IQuickToggleProps {
  isOn: boolean;
  label: string;
  testId: string;
  children: React.ReactNode;
  onToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
}

export const QuickToggle = React.forwardRef<HTMLButtonElement, IQuickToggleProps>(
  (
    { isOn, label, testId, children, onToggle, onMouseDown, ...rest }: IQuickToggleProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={classNames("kuika-quick-toggle", { "kuika-quick-toggle--on": isOn })}
      aria-label={label}
      aria-pressed={isOn}
      data-testid={testId}
      onClick={onToggle}
      onMouseDown={onMouseDown}
    >
      {children}
    </button>
  )
);
QuickToggle.displayName = "QuickToggle";

// ── Boolean chip ────────────────────────────────────────────────────────────────────────────────

export interface IQuickSwitchChipProps {
  isOn: boolean;
  label: string;
  testId: string;
  /** Only the switch — inside a popover row that already names the property. */
  isLabelHidden?: boolean;
  onToggle: () => void;
}

/**
 * A boolean property: its name and a mini switch showing the state (on = accent fill), at the same size
 * as every other chip. `aria-pressed` carries the state for assistive tech.
 */
export const QuickSwitchChip = React.forwardRef<HTMLButtonElement, IQuickSwitchChipProps>(
  (
    { isOn, label, testId, isLabelHidden, onToggle, ...rest }: IQuickSwitchChipProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={classNames("kuika-quick-chip", "kuika-quick-chip--switch", { "kuika-quick-chip--on": isOn })}
      aria-label={label}
      aria-pressed={isOn}
      data-testid={testId}
      onClick={onToggle}
    >
      {!isLabelHidden && <span className="kuika-quick-chip__label">{label}</span>}
      <span className={classNames("kuika-quick-switch", { "kuika-quick-switch--on": isOn })} aria-hidden="true">
        <span className="kuika-quick-switch__thumb" />
      </span>
    </button>
  )
);
QuickSwitchChip.displayName = "QuickSwitchChip";

// ── Swatch ──────────────────────────────────────────────────────────────────────────────────────

export interface IQuickSwatchProps {
  color: string | null | undefined;
}

export const QuickSwatch = ({ color }: IQuickSwatchProps): JSX.Element => (
  <span
    className={classNames("kuika-quick-swatch", { "kuika-quick-swatch--empty": !color })}
    style={color ? { background: color } : undefined}
    aria-hidden="true"
  />
);
